/**
 * Documents global success listener behavior for ERP Accelerator.
 */

"use client";

import React, { useEffect } from 'react';
import { useBanner } from './BannerProvider';

/**
 * GlobalSuccessListener listens for custom success events and displays
 * a persistent success banner. Use anywhere via:
 * document.dispatchEvent(new CustomEvent('app:success', { detail: { message, title, duration } }))
 */
const GlobalSuccessListener: React.FC = () => {
  const { showBanner } = useBanner();

  useEffect(() => {
    const onSuccess = (event: Event) => {
      const anyEvent = event as CustomEvent<{ message?: string; title?: string; duration?: number }>;
      const message = anyEvent?.detail?.message || 'Action completed successfully';
      const title = anyEvent?.detail?.title;
      const duration = anyEvent?.detail?.duration;
      showBanner({ type: 'success', message, title, duration });
    };

    const onInfo = (event: Event) => {
      const anyEvent = event as CustomEvent<{ message?: string; title?: string; duration?: number }>;
      const message = anyEvent?.detail?.message || '';
      const title = anyEvent?.detail?.title;
      const duration = anyEvent?.detail?.duration;
      showBanner({ type: 'info', message, title, duration });
    };

    document.addEventListener('app:success', onSuccess as EventListener);
    document.addEventListener('app:info', onInfo as EventListener);
    return () => {
      document.removeEventListener('app:success', onSuccess as EventListener);
      document.removeEventListener('app:info', onInfo as EventListener);
    };
  }, [showBanner]);

  return null;
};

export default GlobalSuccessListener;
